import React, { ReactElement, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { keycloak } from "../../../lib/keycloak/index.ts";
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hook.ts";
import { OperatorIcon } from "../../../ui/icons/operatorIcon.tsx";
import { LayoutComponent } from "../../../ui/layout/index.tsx";
import { ButtonComponent } from "../../../ui/molecules/button/index.tsx";
import { ErrorPopup } from "../../../ui/organisms/errorPopup/index.tsx";
import { HeadingSize } from "../../../ui/organisms/heading/dto.ts";
import { HeadingComponent } from "../../../ui/organisms/heading/index.tsx";
import { Pagination } from "../../../ui/organisms/pagination/index.tsx";
import { MenuItems } from "../../../ui/organisms/sidebar/dto.ts";
import { SuccessPopup } from "../../../ui/organisms/successPopup/index.tsx";
import { setSelectedTab } from "../slice.ts";
import { CollaboratorFilters } from "./collaboratorFilters.tsx";
import { CollaboratorList } from "./list.tsx";
import { NewCollaboratorModal } from "./newCollaboratorModal.tsx";
import { findAllCollaborator, setCollaboratorFilterPage, setCreateCollaboratorsStatus, setCreated, setDeleteCollaboratorStatus, setEditCollaboratorStatus, setOpenAlert, setOpenNewCollaboratorModal, setSaveOrEdit } from "./slice.ts";
import { findMeFinancialAdvisor } from "../../auth/slice.ts";

export function Collaborator() {
    const dispatch = useAppDispatch()
    const collaboratorState = useAppSelector(state => state.collaborator)
    const financialAdvisorState = useAppSelector(state => state.financialAdvisor)
    const layoutState = useAppSelector(state => state.layout)
    const authState = useAppSelector(state => state.auth)

    const navigate = useNavigate()

    const faId = financialAdvisorState.findByIdFinancialAdvisorResponse ? financialAdvisorState.findByIdFinancialAdvisorResponse.id : ''

    useEffect(() => {
        dispatch(findAllCollaborator({ ...collaboratorState.collaboratorFilters, financialAdvisorId: Number(faId) }))
    }, [collaboratorState.collaboratorFilters.page])

    useEffect(() => {
        if (collaboratorState.createCollaboratorStatus === 'successfully') {
            if (keycloak.hasRealmRole('financial_advisor'))
                dispatch(findMeFinancialAdvisor())
            dispatch(setCreated(true))
            dispatch(setCreateCollaboratorsStatus('idle'))
        }
    }, [collaboratorState.createCollaboratorStatus])

    let body: ReactElement[] = []

    body.push(
        <>
            <SuccessPopup
                active={collaboratorState.created}
                close={() => dispatch(setCreated(false))}
                message="Collaboratore aggiunto"
            />
            <ErrorPopup
                active={collaboratorState.createCollaboratorStatus === "failed"}
                close={() => dispatch(setCreateCollaboratorsStatus("idle"))}
                message="Errore durante la creazione del collaboratore."
            />
            <SuccessPopup
                active={collaboratorState.editCollaboratorStatus === "successfully"}
                close={() => dispatch(setEditCollaboratorStatus("idle"))}
                message="Collaboratore modificato"
            />
            <ErrorPopup
                active={collaboratorState.editCollaboratorStatus === "failed"}
                close={() => dispatch(setEditCollaboratorStatus("idle"))}
                message="Errore durante la modifica del collaboratore."
            />
            <SuccessPopup
                active={collaboratorState.deleteCollaboratorStatus === "successfully"}
                close={() => dispatch(setDeleteCollaboratorStatus("idle"))}
                message="Collaboratore eliminato"
            />
            <ErrorPopup
                active={collaboratorState.deleteCollaboratorStatus === "failed"}
                close={() => dispatch(setDeleteCollaboratorStatus("idle"))}
                message="Errore durante l'eliminazione del collaboratore."
            />
            <ErrorPopup active={collaboratorState.openAlert}
                close={() => { dispatch(setOpenAlert(false)) }}
                message="Hai raggiunto il numero max. di collaboratori!"
                cta={<span className=" cursor-pointer text-warning-400 font-semibold text-text-md text-center" onClick={() => {
                    dispatch(setSelectedTab(2))
                    if (keycloak.hasRealmRole('financial_advisor')) {
                        dispatch(setSelectedTab(2)); navigate('/setting')
                    }
                }}>Aggiorna il piano</span>
                }
            />
            <CollaboratorFilters />
            <CollaboratorList />
            {
                collaboratorState.findAllCollaboratorResponse !== undefined &&
                collaboratorState.findAllCollaboratorResponse?.total > 0 &&
                <div className="flex bg-white drop-shadow-sm items-center justify-center p-2">
                    <Pagination
                        pages={collaboratorState.findAllCollaboratorResponse?.totalPage || 1}
                        currentPage={collaboratorState.collaboratorFilters.page}
                        setPage={(page) => dispatch(setCollaboratorFilterPage(page))}
                    />
                </div>
            }
            <NewCollaboratorModal />
        </>
    )

    return (
        <>
            {
                keycloak.hasRealmRole("financial_advisor") ?
                    <LayoutComponent
                        breadcrumbItems={['Collaboratori']}
                        headingLabel={"Collaboratori"}
                        headingButtons={[
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 16 }}>
                                <ButtonComponent
                                    label={"Nuovo collaboratore"}
                                    onClick={() => {
                                        dispatch(setSaveOrEdit('save'))
                                        if (!((keycloak.hasRealmRole('financial_advisor') && authState.findMeFinancialAdvisorResponse?.collaborators.length === authState.findMeFinancialAdvisorResponse?.maxNumberOfCollaborators)
                                            || (keycloak.hasRealmRole('admin') && financialAdvisorState.findByIdFinancialAdvisorResponse!.collaborators.length === financialAdvisorState.findByIdFinancialAdvisorResponse?.maxNumberOfCollaborators)))
                                            dispatch(setOpenNewCollaboratorModal(true))
                                        else dispatch(setOpenAlert(true))
                                    }}
                                    color={"blue"}
                                    variant={"solid"}
                                    size={"md"}
                                    iconPosition="off"
                                    disabled={!authState.findMeFinancialAdvisorResponse?.isActive || authState.findMeCollaboratorResponse?.isActive}
                                />
                            </div>
                        ]}
                        menuItem={MenuItems.COLLABORATOR}
                        breadcrumbIcon={<OperatorIcon color={layoutState.theme[500]} size={"20"} />}
                    >
                        {body}
                    </LayoutComponent>
                    :
                    <div className={"flex flex-col gap-3 flex-grow"}>
                        <HeadingComponent
                            label={"Collaboratori"}
                            buttons={[
                                <ButtonComponent
                                    label={"Aggiungi collaboratore"}
                                    onClick={() => {
                                        dispatch(setSaveOrEdit('save'))
                                        if (!((keycloak.hasRealmRole('financial_advisor') && authState.findMeFinancialAdvisorResponse?.collaborators.length === authState.findMeFinancialAdvisorResponse?.maxNumberOfCollaborators)
                                            || ((keycloak.hasRealmRole('admin') || keycloak.hasRealmRole('operator')) && financialAdvisorState.findByIdFinancialAdvisorResponse!.collaborators.length === financialAdvisorState.findByIdFinancialAdvisorResponse!.maxNumberOfCollaborators)))
                                            dispatch(setOpenNewCollaboratorModal(true))
                                        else dispatch(setOpenAlert(true))
                                    }}
                                    color={"blue"}
                                    variant={"solid"}
                                    size={"md"}
                                    iconPosition="off"
                                />
                            ]}
                            size={HeadingSize.LG}
                        />
                        {body}
                    </div>
            }
        </>
    )
}